import { task } from 'mobx-task'
import InventoryStore, {
  OverrideInventoryItemParams,
} from '../../../InventoryStore'
import Workspace from '../../../../iam/models/Workspace'
import { AlertFactory, AlertState } from '../../../../alerts/Alert'
import WorkspaceSelectorViewModel from '../../WorkspaceSelectorViewModel'
import { action, observable, makeObservable, override } from 'mobx'
import { BaseInventoryItemViewModel } from '../BaseInventoryItemDialog/BaseInventoryItemViewModel'
import { validationContext } from 'validx'
import { IWithWorkspaceSelection } from '../../components/InventoryItemFormWorkpaceDropdown'
import { RefreshInventoryItems } from 'admin/inventory/InventorySearchViewModel'
import { SupportingItemsPorts } from '../../../ports'

export default class OverrideInventoryItemViewModel
  extends BaseInventoryItemViewModel
  implements IWithWorkspaceSelection
{
  /**
   * The selected workspace
   */
  @observable workspace? = Workspace.none

  /**
   * Validations for the workspace
   */
  workspaceValidation = validationContext(this, {
    workspace: [(opts) => !!opts.value || 'Workspace is required'],
  })

  constructor(
    private inventoryStore: InventoryStore,
    private newAlert: AlertFactory,
    private refreshItems: RefreshInventoryItems,
    public getSelectedWorkspace: () => Workspace | undefined,
    public workspaceSelectorVM: WorkspaceSelectorViewModel,
    supportingItemsAdapter: SupportingItemsPorts,
  ) {
    super(supportingItemsAdapter)
    makeObservable(this)
  }

  /**
   * Shows the dialog
   */
  @override
  show() {
    this.workspace = this.getSelectedWorkspace()
    super.show()
  }

  /**
   * Set the workspace for the new item
   */
  @action.bound
  setWorkspace(workspace?: Workspace) {
    this.workspace = workspace
  }

  submit = task.resolved(async () => {
    if (
      !this.validate() ||
      !this.workspaceValidation.reset().validate().isValid
    ) {
      return
    }

    const typeSpec = this.getInventoryItemTypeSpecification()

    if (this.item && this.workspace) {
      const pricing = this.pricingViewModel.toValues()

      const overriddenItem: OverrideInventoryItemParams = {
        name: this.item.name,
        description: this.item.description,
        courtesyAmount: this.courtesyQuantity,
        pricing,
        workspace: this.workspace.id!,
        rootId: this.item.id,
        supportingItems: this.supportingItems,
        documentSignatureItems: this.documentSignatureItems,
        hideFromPro: this.hideFromPro,
        typeSpecification: typeSpec,
      }

      await this.inventoryStore
        .override(overriddenItem)
        .then(async () => {
          await this.refreshItems(this.workspace || Workspace.none)

          this.newAlert()
            .withState(AlertState.Success)
            .saying('Success', 'Item Overridden.')
            .autoDismiss()
            .show()

          this.close()
        })
        .catch((e) => {
          this.newAlert()
            .withState(AlertState.Error)
            .saying('Error', e.message)
            .autoDismiss(2000)
            .show()
        })
    }
  })
}
